import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import { PageShell } from "../PageShell";
import { SetupFlow } from "./SetupFlow";
import { ConnectedHealthSystems } from "./ConnectedHealthSystems";
import { PatientLinkPreview } from "./PatientLinkPreview";
import {
  usePatientConnectionsQuery,
  usePatientRecommendedSourcesQuery,
} from "@/frontend/queries/dashboard/patients";
import { patientNameInSentence } from "@/frontend/utils/patient-name";
import { ONBOARDING_TABS, type OnboardingTabId } from "@/frontend/types/dashboard/nav";

/**
 * The Onboarding page for a workspace that onboards other people. Setup holds
 * the three numbered steps beside a live preview of what the patient walks
 * through, and Connected systems holds the health systems already linked. Each
 * tab is a path segment, so a reload or a shared link opens the right tab.
 *
 * @param patientMembershipId the patient being onboarded
 * @param patientFirstName their given name, or null when none is on file
 * @param patientFullName their full name, for the preview's greeting
 * @param askConsentSignature whether the link asks for a signature
 * @param showMobileApp whether the link offers the mobile app
 * @param canManage whether this member may change the setup
 * @param activeTab the tab currently showing, from the URL
 */
export function OnboardingTabs({
  patientMembershipId,
  patientFirstName,
  patientFullName,
  askConsentSignature,
  showMobileApp,
  canManage,
  activeTab,
}: {
  patientMembershipId: string;
  patientFirstName: string | null;
  patientFullName?: string | null;
  askConsentSignature: boolean;
  showMobileApp: boolean;
  canManage: boolean;
  activeTab: OnboardingTabId;
}) {
  const { data: sources } = usePatientRecommendedSourcesQuery(patientMembershipId);
  const { data: connectionList } = usePatientConnectionsQuery(patientMembershipId);

  const patient = patientNameInSentence(patientFirstName);
  const connectedCount = connectionList?.connections.length ?? 0;

  return (
    // The route owns the viewport frame; this fills whatever height it is given.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b">
        <PageShell className="pt-2">
          <div role="tablist" aria-label="Onboarding sections" className="-mb-px flex gap-6">
            {ONBOARDING_TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <Link
                  key={tab.id}
                  to="/dashboard/patients/$patientId/onboarding/$tab"
                  params={{ patientId: patientMembershipId, tab: tab.id }}
                  replace
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    "flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 pb-3 pt-1.5 text-body transition-colors",
                    isActive
                      ? "border-primary font-semibold text-primary"
                      : "border-transparent font-medium text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab.label}
                  {tab.id === "connected-systems" && connectedCount > 0 && (
                    <span className="tabular-nums text-muted-foreground">{connectedCount}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </PageShell>
      </div>

      {activeTab === "setup" ? (
        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* The settings column owns the page gutters, and scrolls on its own so
              a long settings list never pushes the preview off screen. */}
          <div className="min-w-0 lg:overflow-y-auto">
            <PageShell className="py-6">
              <SetupFlow
                patientMembershipId={patientMembershipId}
                patientFirstName={patientFirstName}
                askConsentSignature={askConsentSignature}
                showMobileApp={showMobileApp}
                canManage={canManage}
              />
            </PageShell>
          </div>

          {/* The muted region is the whole column, edge to edge, and the phone
              inside it is the only thing on this side that ever scrolls. */}
          <div className="flex min-h-0 flex-col bg-muted/30 lg:border-l lg:border-border/60">
            <PatientLinkPreview
              patientFirstName={patientFirstName}
              patientFullName={patientFullName}
              askConsentSignature={askConsentSignature}
              showMobileApp={showMobileApp}
              suggestedSources={(sources ?? []).map((source) => ({
                name: source.name,
                logo: source.logo,
              }))}
            />
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 lg:overflow-y-auto">
          <PageShell className="py-6">
            <ConnectedHealthSystems
              patientId={patientMembershipId}
              whenEmpty={
                <div className="flex flex-col items-center justify-center rounded-lg border py-16 text-center">
                  <Building2 className="mb-3 size-6 text-muted-foreground opacity-60" />
                  <p className="text-caption font-medium">No health systems connected yet</p>
                  <p className="mt-1 max-w-sm text-caption text-muted-foreground">
                    They appear here as <span data-patient>{patient}</span> connects to them
                  </p>
                </div>
              }
            />
          </PageShell>
        </div>
      )}
    </div>
  );
}
